<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-4'
      ]"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <button
            :class="[
              theme.isDark.value
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
              'p-2 rounded-lg transition-all'
            ]"
            title="Back to Compliance Breakdown"
            @click="goBack"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <div>
          <h1 :class="[theme.isDark.value ? 'text-gray-100' : 'text-gray-900', 'text-2xl font-bold']">
            Compliance PRs
          </h1>
          <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'mt-1 text-sm']">
            Pull requests in the selected compliance scope
          </p>
          </div>
        </div>
        <button
          :disabled="loading"
          :class="[
            theme.isDark.value
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              : 'bg-white hover:bg-gray-50 text-gray-700',
            'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors flex items-center',
            loading ? 'opacity-50 cursor-not-allowed' : ''
          ]"
          @click="handleRefresh"
        >
          <svg
            :class="[loading ? 'animate-spin' : '', 'w-4 h-4 mr-2']"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <div class="p-6 space-y-6">
      <div
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-md p-3'
        ]"
      >
        <div class="flex flex-wrap items-center gap-4">
          <div class="flex items-center space-x-2">
            <span :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-xs font-medium']">
              Time Range:
            </span>
            <select
              v-model="timeRange"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]"
              @change="handleFilterChange"
            >
              <option value="7d">Last 7 Days</option>
              <option value="14d">Last 14 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>

          <div :class="[theme.isDark.value ? 'bg-gray-700' : 'bg-gray-300', 'w-px h-6']"></div>

          <div class="flex items-center space-x-2">
            <span :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-xs font-medium']">
              Team:
            </span>
            <select
              v-model="team"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]"
              @change="onTeamChange"
            >
              <option
                v-for="teamName in teams"
                :key="teamName"
                :value="teamName"
              >
                {{ leaderboardService.formatTeamName(teamName) }}
              </option>
            </select>
          </div>

          <div class="flex items-center space-x-2">
            <span :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-xs font-medium']">
              Sub-Team:
            </span>
            <select
              v-model="subTeam"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]"
              @change="handleFilterChange"
            >
              <option value="">All Sub-Teams</option>
              <option
                v-for="subTeamName in subTeams"
                :key="subTeamName"
                :value="subTeamName"
              >
                {{ leaderboardService.formatSubTeamName(subTeamName) }}
              </option>
            </select>
          </div>

          <div class="flex items-center space-x-2">
            <span :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-xs font-medium']">
              Quality Gate:
            </span>
            <select
              v-model="qualityGate"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                'px-3 py-1.5 border rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer'
              ]"
              @change="handleFilterChange"
            >
              <option value="">All Gates</option>
              <option
                v-for="gate in qualityGates"
                :key="gate"
                :value="gate"
              >
                {{ leaderboardService.formatGateName(gate) }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div
        v-if="error"
        :class="[
          theme.isDark.value ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200',
          'border rounded-md p-4'
        ]"
      >
        <div class="text-sm text-red-700 dark:text-red-200">{{ error }}</div>
      </div>

      <div
        v-if="loading && prs.length === 0"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8'
        ]"
      >
        <div class="flex items-center justify-center">
          <svg class="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span class="ml-3 text-lg text-gray-600 dark:text-gray-300">Loading PRs...</span>
        </div>
      </div>

      <div
        v-else-if="prs.length > 0"
        class="space-y-4"
      >
        <div
          v-for="pr in prs"
          :key="`${pr.repository}-${pr.pr_number}`"
          :class="[
            theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer',
            `border-l-4 ${qualityGatesService.getStatusBorderColor(pr.status)}`
          ]"
          @click="navigateToPR(pr)"
        >
          <div class="p-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-4">
                <span
                  :class="[
                    qualityGatesService.getStatusColor(pr.status),
                    'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium'
                  ]"
                >
                  <span class="mr-1">{{ qualityGatesService.getStatusIcon(pr.status) }}</span>
                  {{ pr.status.toUpperCase() }}
                </span>
                <div>
                  <div class="flex items-center space-x-2">
                    <span class="font-mono text-sm font-medium text-gray-900 dark:text-white">
                      {{ pr.repository }}
                    </span>
                    <span class="text-gray-500 dark:text-gray-400">#{{ pr.pr_number }}</span>
                  </div>
                  <div class="text-sm text-gray-500 dark:text-gray-400">
                    {{ qualityGatesService.formatRelativeTime(pr.last_updated) }}
                    · {{ pr.commit_sha.substring(0, 7) }}
                  </div>
                </div>
              </div>
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>

        <div
          v-if="pagination.hasMore"
          class="flex justify-center pt-2"
        >
          <button
            :disabled="loading"
            :class="[
              theme.isDark.value
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                : 'bg-white hover:bg-gray-50 text-gray-700',
              'px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors',
              loading ? 'opacity-50 cursor-not-allowed' : ''
            ]"
            @click="loadMore"
          >
            {{ loading ? 'Loading...' : 'Load More' }}
          </button>
        </div>
      </div>

      <div
        v-else-if="!loading && !error"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8 text-center'
        ]"
      >
        <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No results found</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          No pull requests match the selected compliance scope.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { qualityGatesService, leaderboardService } from '../../services/quality-gates'
import type { QualityGatePR, TimeRange } from '../../types/quality-gates'
import { useTheme } from '../../utils/useTheme'

const route = useRoute()
const router = useRouter()
const theme = inject('theme', useTheme())

const prs = ref<QualityGatePR[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const pagination = ref({
  hasMore: false,
  nextCursor: undefined as string | undefined
})
const teams = ref<string[]>([])
const subTeams = ref<string[]>([])
const qualityGates = ref<string[]>([])

const team = ref('')
const subTeam = ref('')
const qualityGate = ref('')
const timeRange = ref<TimeRange>('7d')

const updateURL = () => {
  const query: Record<string, string> = {
    team: team.value,
    timeRange: timeRange.value
  }
  if (subTeam.value) query.subTeam = subTeam.value
  if (qualityGate.value) query.qualityGate = qualityGate.value
  router.replace({ query })
}

const fetchCompliancePRs = async (append = false) => {
  if (!team.value) {
    error.value = 'Team is required for compliance PR listing.'
    prs.value = []
    pagination.value = { hasMore: false, nextCursor: undefined }
    return
  }

  try {
    loading.value = true
    error.value = null
    if (!append) {
      updateURL()
    }
    const response = await qualityGatesService.fetchCompliancePRs({
      team: team.value,
      subTeam: subTeam.value || undefined,
      qualityGate: qualityGate.value || undefined,
      timeRange: timeRange.value,
      limit: 20,
      cursor: append ? pagination.value.nextCursor : undefined
    })
    if (append) {
      prs.value.push(...response.data)
    } else {
      prs.value = response.data
    }
    pagination.value = {
      hasMore: response.pagination.hasMore,
      nextCursor: response.pagination.nextCursor
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load PRs'
    prs.value = []
    pagination.value = { hasMore: false, nextCursor: undefined }
  } finally {
    loading.value = false
  }
}

const fetchFilterOptions = async () => {
  try {
    const [teamsData, gatesData] = await Promise.all([
      leaderboardService.fetchTeams(),
      leaderboardService.fetchQualityGates()
    ])
    teams.value = teamsData.teams
    subTeams.value = teamsData.subTeams
    qualityGates.value = gatesData.gates

    if (!team.value && teams.value.length > 0) {
      team.value = teams.value[0]
    }
  } catch {
    // keep current values; page can still function with URL provided filters
  }
}

const onTeamChange = () => {
  subTeam.value = ''
  fetchCompliancePRs()
}

const handleFilterChange = () => {
  void fetchCompliancePRs()
}

const handleRefresh = () => {
  void fetchCompliancePRs()
}

const loadMore = () => {
  if (!pagination.value.hasMore || !pagination.value.nextCursor || loading.value) return
  fetchCompliancePRs(true)
}

const navigateToPR = (pr: QualityGatePR) => {
  router.push(`/quality-gates/${encodeURIComponent(pr.repository)}/${pr.pr_number}`)
}

const goBack = () => {
  const query: Record<string, string> = {
    team: team.value,
    timeRange: timeRange.value
  }

  if (subTeam.value) query.subTeam = subTeam.value
  if (qualityGate.value) query.qualityGate = qualityGate.value

  router.push({
    path: '/quality-gates/compliance/details',
    query
  })
}

onMounted(() => {
  team.value = (route.query.team as string) || ''
  subTeam.value = (route.query.subTeam as string) || ''
  qualityGate.value = (route.query.qualityGate as string) || ''
  timeRange.value = ((route.query.timeRange as TimeRange) || '7d') as TimeRange
  fetchFilterOptions()
  fetchCompliancePRs()
})

watch(
  () => route.query,
  (newQuery) => {
    const nextTeam = (newQuery.team as string) || ''
    const nextSubTeam = (newQuery.subTeam as string) || ''
    const nextQualityGate = (newQuery.qualityGate as string) || ''
    const nextTimeRange = ((newQuery.timeRange as TimeRange) || '7d') as TimeRange

    const queryChanged =
      nextTeam !== team.value ||
      nextSubTeam !== subTeam.value ||
      nextQualityGate !== qualityGate.value ||
      nextTimeRange !== timeRange.value

    if (queryChanged) {
      team.value = nextTeam
      subTeam.value = nextSubTeam
      qualityGate.value = nextQualityGate
      timeRange.value = nextTimeRange
      fetchCompliancePRs()
    }
  }
)
</script>
